import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@repo/ui'
import { FileText, Library, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { fetchRagFiles } from '@/api/content/file'
import { fetchRagTexts } from '@/api/content/text'
import { getProposalSources, replaceProposalSources } from '@/api/proposals/proposals'
import { useSourceDocumentLabel } from '@/common/useSourceDocumentLabel'
import { ProposalSourceSelector } from './ProposalSourceSelector'
import { isProposalSourceFile } from './proposalFiles'
import { textPreview } from './proposalText'
import type { ProposalSourceItem } from './ProposalSourceSelector'
import type { ReactNode } from 'react'

export function ProposalSourcesPanel({
  projectId,
  proposalId,
  templateDocId,
  onChange,
  open,
  onOpenChange,
  trigger,
  onCloseAutoFocus
}: {
  projectId: number
  proposalId: number
  templateDocId: number | null | undefined
  onChange: (count: number) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger?: ReactNode
  onCloseAutoFocus?: (event: Event) => void
}) {
  const [documents, setDocuments] = useState<Array<ProposalSourceItem>>([])
  const [texts, setTexts] = useState<Array<ProposalSourceItem>>([])
  const [selected, setSelected] = useState<Array<number>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([
      fetchRagFiles({ projectId, currentPage: 1 }),
      fetchRagTexts({ projectId, currentPage: 1 }),
      getProposalSources(projectId, proposalId)
    ])
      .then(([fileRes, textRes, pinned]) => {
        if (cancelled) return
        setDocuments(
          fileRes.items
            .filter((f) => isProposalSourceFile(f.fileName))
            .map((f) => ({ id: f.fileId, label: f.fileName }))
        )
        setTexts(textRes.items.map((t) => ({ id: t.docId, label: textPreview(t.text) })))
        const nextSelected = pinned.map((p) => p.docId)
        setSelected(nextSelected)
        onChange(nextSelected.length)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load sources')
      })
    return () => {
      cancelled = true
    }
  }, [projectId, proposalId, onChange])

  async function updateSources(next: Array<number>) {
    const previous = selected
    setSelected(next)
    setSaving(true)
    setError(null)
    try {
      await replaceProposalSources(projectId, proposalId, next)
      onChange(next.length)
    } catch {
      setSelected(previous)
      setError('Could not update sources')
    } finally {
      setSaving(false)
    }
  }

  const allSourceItems = useMemo(() => [...documents, ...texts], [documents, texts])
  const selectedItems = useMemo(
    () => selected.map((id) => allSourceItems.find((i) => i.id === id) ?? { id, label: `Source ${id}` }),
    [allSourceItems, selected]
  )
  const templateLabel = useSourceDocumentLabel({ docId: templateDocId, absentLabel: 'No template recorded' })
  const sourceCountLabel = selected.length === 1 ? '1 source' : `${selected.length} sources`

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next)
      }}
    >
      {trigger !== null && (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button variant="outline" size="sm" className="shrink-0" aria-label={`Sources ${selected.length}`}>
              <Library className="size-4" />
              Sources
              <Badge variant="secondary">{selected.length}</Badge>
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent
        onCloseAutoFocus={onCloseAutoFocus}
        showCloseButton={false}
        className="flex max-h-[calc(80vh)] flex-col gap-0 overflow-y-scroll p-0 sm:max-w-3xl"
      >
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle>Sources</DialogTitle>
          <DialogDescription>
            Choose workspace files or text for this proposal. These sources are separate from chat attachments. To add
            new content, upload it in chat or add it to workspace Content, then select it here.
          </DialogDescription>
        </DialogHeader>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute top-3 right-3"
          aria-label="Close"
          disabled={saving}
          onClick={() => onOpenChange(false)}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>

        <div className="min-h-0 flex-1 p-6">
          <div className="space-y-5 pb-6">
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Template</h3>
              <div className="rounded-md border bg-muted/30 p-3">
                <p className="text-xs font-medium text-muted-foreground">Proposal template</p>
                <div className="mt-2 flex min-w-0 items-center gap-2 text-sm">
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate" title={templateLabel}>
                    {templateLabel}
                  </span>
                </div>
              </div>
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">Selected sources</h3>
                <span className="text-xs text-muted-foreground">{sourceCountLabel}</span>
              </div>
              <div className="rounded-md border bg-muted/20">
                {selectedItems.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">No source content selected.</p>
                ) : (
                  <div className="divide-y">
                    {selectedItems.map((item) => (
                      <div key={item.id} className="flex min-w-0 items-center justify-between gap-3 p-2">
                        <span className="min-w-0 truncate text-sm">{item.label}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          onClick={() => void updateSources(selected.filter((id) => id !== item.id))}
                          disabled={saving}
                          aria-label={`Remove selected source ${item.label}`}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <ProposalSourceSelector
              heading="Files"
              items={documents}
              selectedIds={selected}
              onSelectedIdsChange={(ids) => void updateSources(ids)}
              disabled={saving}
              showSelectedItems={false}
            />
            <ProposalSourceSelector
              heading="Text"
              items={texts}
              selectedIds={selected}
              onSelectedIdsChange={(ids) => void updateSources(ids)}
              disabled={saving}
              emptyText="No text content found."
              searchPlaceholder="Search text"
              noSelectionText="No text selected."
              showSelectedItems={false}
            />
          </div>
        </div>
        {saving && (
          <p role="status" className="border-t px-6 py-3 text-sm text-muted-foreground">
            Saving sources...
          </p>
        )}
        {error && (
          <p role="alert" className="border-t px-6 py-3 text-sm text-destructive">
            {error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
